/**
 * Galaxy Ranger — accessible audio frontend.
 *
 * State machine drives a chain of menus and unit-driver modes. Sounds carry
 * the gameplay; speech is reserved for menu items, explicit player queries
 * (energy, position, score), error messages, and game-over.
 */

import {
  type Unit,
  Difficulty,
  Game,
  UNIT_DATA,
  UnitKind,
  WEAPONS,
  chebyshev,
  kindName,
  recommendWeapon,
  shipHeight,
} from "@galaxy-ranger/core";
import { AudioEngine, type SpatialPan } from "./audio.js";
import { SpeechEngine } from "./speech.js";
import { SOUNDS, driverAmbient, driverSound, enemyIdSound } from "./sound-map.js";

// ── Screen state machine ──────────────────────────────────────────────────

type Screen =
  | { kind: "idle" }
  | { kind: "main_menu"; index: number }
  | { kind: "difficulty"; index: number }
  | { kind: "command_post"; index: number }
  | { kind: "factory"; index: number }
  | { kind: "placement" }
  | { kind: "driver" }
  | { kind: "weapon_menu"; index: number }
  | { kind: "messages"; readingArchive: boolean }
  | { kind: "game_over"; outcome: "won" | "lost" };

const MAIN_MENU_ITEMS: ReadonlyArray<keyof typeof MAIN_MENU_LABELS> = [
  "play_with_computer",
  "play_with_partner",
  "learn_game_sounds",
  "final_score",
  "send_score",
  "test_speakers",
  "get_id",
  "quit",
] as const;

const MAIN_MENU_LABELS = {
  play_with_computer: "Play with the computer",
  play_with_partner: "Play with a partner",
  learn_game_sounds: "Learn game sounds",
  final_score: "Final score",
  send_score: "Send score",
  test_speakers: "Test speakers",
  get_id: "Get your ID",
  quit: "Quit game",
} as const;

const DIFFICULTY_OPTIONS: ReadonlyArray<{ d: Difficulty; label: string }> = [
  { d: Difficulty.Easy, label: "Easy" },
  { d: Difficulty.Normal, label: "Normal" },
  { d: Difficulty.Hard, label: "Hard" },
  { d: Difficulty.Super, label: "Super" },
  { d: Difficulty.Insane, label: "Insane" },
] as const;

const COMMAND_POST_ITEMS: ReadonlyArray<{ kind: "factory" | "kind" | "messages" | "stop"; data?: UnitKind }> = [
  { kind: "factory" },
  { kind: "kind", data: UnitKind.Worker },
  { kind: "kind", data: UnitKind.Truck },
  { kind: "kind", data: UnitKind.Soldier },
  { kind: "kind", data: UnitKind.ElectronicGun },
  { kind: "kind", data: UnitKind.RocketPlant },
  { kind: "kind", data: UnitKind.SpaceTank },
  { kind: "kind", data: UnitKind.SpaceScout },
  { kind: "kind", data: UnitKind.SpaceCruiser },
  { kind: "messages" },
  { kind: "stop" },
];

const FACTORY_ITEMS: ReadonlyArray<{ kind: "status" } | { kind: "build"; data: UnitKind }> = [
  { kind: "status" },
  { kind: "build", data: UnitKind.Worker },
  { kind: "build", data: UnitKind.Truck },
  { kind: "build", data: UnitKind.Soldier },
  { kind: "build", data: UnitKind.ElectronicGun },
  { kind: "build", data: UnitKind.RocketPlant },
  { kind: "build", data: UnitKind.SpaceTank },
  { kind: "build", data: UnitKind.SpaceScout },
  { kind: "build", data: UnitKind.SpaceCruiser },
];

// ── App ───────────────────────────────────────────────────────────────────

class App {
  game: Game | null = null;
  screen: Screen = { kind: "idle" };
  audio = new AudioEngine();
  speech: SpeechEngine;
  /** ms between game ticks while in driver mode. */
  private tickIntervalMs = 1000;
  private tickTimer: number | null = null;

  private titleEl: HTMLElement;
  private currentEl: HTMLElement;
  private hintEl: HTMLElement;

  constructor(titleEl: HTMLElement, currentEl: HTMLElement, hintEl: HTMLElement) {
    this.titleEl = titleEl;
    this.currentEl = currentEl;
    this.hintEl = hintEl;
    this.speech = new SpeechEngine();
  }

  /**
   * Play a pre-recorded WAV when one exists for this item, fall back to
   * speech synthesis otherwise. Routed through the audio engine's voice
   * channel so rapid menu navigation cleanly interrupts the previous
   * utterance — only one "voice" plays at a time.
   */
  private narrate(label: string, samplePath?: string): void {
    this.speech.cancel();
    if (samplePath) {
      this.audio.playUntilEnd(samplePath, { gain: 0.9 });
    } else {
      this.audio.stopVoice();
      this.speech.say(label);
    }
  }

  async startGame(): Promise<void> {
    await this.audio.start();
    // Per help.txt:79 the original sequence is: clock sound (mandatory while
    // sounds load) → game intro (skippable with Ctrl) → main menu.
    this.titleEl.textContent = "Loading…";
    await this.audio.playUntilEnd(SOUNDS.clock, { gain: 0.6 });
    this.titleEl.textContent = "Welcome…";
    await this.playWithCtrlSkip(SOUNDS.intro, { gain: 0.7 });
    this.transition({ kind: "main_menu", index: 0 });
  }

  /**
   * Play a voice sample to completion, but resolve early if the player
   * presses Ctrl. Used for the intro per help.txt:79 ("you can hit the
   * ctrl key to skip this sound").
   */
  private playWithCtrlSkip(path: string, options: { gain?: number } = {}): Promise<void> {
    return new Promise<void>((resolve) => {
      let done = false;
      const handler = (ev: KeyboardEvent): void => {
        if (done) return;
        if (ev.key !== "Control" && ev.code !== "ControlLeft" && ev.code !== "ControlRight") return;
        done = true;
        ev.preventDefault();
        document.removeEventListener("keydown", handler, true);
        this.audio.stopVoice();
        resolve();
      };
      document.addEventListener("keydown", handler, true);
      this.audio.playUntilEnd(path, options).then(() => {
        if (done) return;
        done = true;
        document.removeEventListener("keydown", handler, true);
        resolve();
      });
    });
  }

  /** Set the visible "currently focused item" line. Doesn't speak. */
  private setCurrent(text: string): void {
    this.currentEl.textContent = text;
  }

  private transition(s: Screen): void {
    this.screen = s;
    // The hint and title are per-screen-type, not per-item. Set them once
    // here so screen readers don't re-announce them on every arrow press.
    switch (s.kind) {
      case "main_menu":
        this.audio.stopAllLoops();
        this.audio.loop("bg", SOUNDS.bgMenu, { gain: 0.08 });
        this.titleEl.textContent = "Main menu";
        this.setHint("Up and Down to navigate. Enter to select.");
        // Play the orientation cue ("use up and down arrow keys…") once on
        // entry, then announce the first item after it finishes. If the
        // player navigates while the cue is still playing, the renderMainMenu
        // guard at the end skips the auto-announce.
        this.audio.playUntilEnd(SOUNDS.menuArrows).then(() => {
          if (this.screen.kind === "main_menu" && this.screen.index === 0) this.renderMainMenu();
        });
        break;
      case "difficulty":
        this.titleEl.textContent = "Choose difficulty";
        this.setHint("Up and Down to navigate. Enter to start. Escape to return.");
        this.audio.playUntilEnd(SOUNDS.diffArrows).then(() => {
          if (this.screen.kind === "difficulty" && this.screen.index === 0) this.renderDifficulty();
        });
        break;
      case "command_post":
        this.audio.stopLoop("bg");
        this.audio.loop("bg", SOUNDS.bgGame, { gain: 0.08 });
        this.titleEl.textContent = "Command post";
        this.setHint("Up/Down navigate. Enter choose. Space query count. B base. Ctrl+N read next message.");
        this.renderCommandPost();
        break;
      case "factory":
        this.titleEl.textContent = "Factory";
        this.setHint("Up/Down navigate. Space speaks cost. Enter produces. Escape returns.");
        this.renderFactory();
        break;
      case "placement":
        this.setHint("Arrow keys to move within two cells of base. Enter to set position.");
        this.renderPlacement();
        break;
      case "driver":
        if (this.game?.driverKind != null) {
          this.audio.stopLoop("bg");
          this.audio.loop("bg", driverAmbient(this.game.driverKind), { gain: 0.08 });
        }
        this.setHint(
          "↑ start move, ↓ stop, ←/→ turn or steer, R radar, W weapon, " +
            "Enter fire, S shield, Space pos, Tab energy, D dir, K enemy, " +
            "Z weapon hint, T teleport, X strike, Esc back.",
        );
        this.renderDriver();
        break;
      case "weapon_menu":
        this.titleEl.textContent = "Weapon menu";
        this.setHint("Up/Down navigate. Enter fires. Escape cancels.");
        this.renderWeaponMenu();
        break;
      case "messages":
        this.setHint("Down reads next. Space repeats. Delete removes. O switches new/old. Escape returns.");
        this.renderMessages(s);
        break;
      case "game_over":
        this.audio.stopAllLoops();
        this.audio.play(SOUNDS.gameOver);
        this.speech.say(s.outcome === "won" ? "You win!" : "You lose.");
        this.titleEl.textContent = s.outcome === "won" ? "Victory" : "Defeat";
        this.setHint("Press Enter to return to the main menu.");
        break;
      case "idle":
        break;
    }
  }

  /** Idempotent hint setter — leaves the DOM alone if unchanged. */
  private currentHint = "";
  private setHint(text: string): void {
    if (text === this.currentHint) return;
    this.currentHint = text;
    this.hintEl.textContent = text;
  }

  // ── Main menu ────────────────────────────────────────────────────────────

  private mainItem(i: number): keyof typeof MAIN_MENU_LABELS {
    return MAIN_MENU_ITEMS[i % MAIN_MENU_ITEMS.length]!;
  }

  private renderMainMenu(): void {
    if (this.screen.kind !== "main_menu") return;
    const key = this.mainItem(this.screen.index);
    const label = MAIN_MENU_LABELS[key];
    this.setCurrent(label);
    const samples: Record<keyof typeof MAIN_MENU_LABELS, string> = {
      play_with_computer: SOUNDS.startGame,
      play_with_partner: SOUNDS.playWithPartner,
      learn_game_sounds: SOUNDS.learnGameSounds,
      final_score: SOUNDS.finalScore,
      send_score: SOUNDS.sendScore,
      test_speakers: SOUNDS.testSpeakers,
      get_id: SOUNDS.getId,
      quit: SOUNDS.quit,
    };
    this.narrate(label, samples[key]);
  }

  // ── Difficulty ───────────────────────────────────────────────────────────

  private renderDifficulty(): void {
    if (this.screen.kind !== "difficulty") return;
    const item = DIFFICULTY_OPTIONS[this.screen.index]!;
    this.setCurrent(item.label);
    const samples: Partial<Record<Difficulty, string>> = {
      [Difficulty.Easy]: SOUNDS.diffEasy,
      [Difficulty.Normal]: SOUNDS.diffMiddle,
      [Difficulty.Hard]: SOUNDS.diffHard,
      [Difficulty.Super]: SOUNDS.diffSuper,
      [Difficulty.Insane]: SOUNDS.diffInsane,
    };
    this.narrate(item.label, samples[item.d]);
  }

  private startNewGame(d: Difficulty): void {
    const seed = (Math.random() * 2 ** 31) | 0;
    // Seed an opponent roster so the AI threat is interesting.
    const tempGame = new Game({ difficulty: d, seed });
    const opp = tempGame.board.bases[1];
    const roster = [
      { kind: UnitKind.Soldier, position: { x: opp.x + 1, y: opp.y } },
      { kind: UnitKind.Soldier, position: { x: opp.x + 2, y: opp.y } },
      { kind: UnitKind.Soldier, position: { x: opp.x, y: opp.y + 1 } },
      { kind: UnitKind.SpaceTank, position: { x: opp.x - 1, y: opp.y } },
      { kind: UnitKind.SpaceCruiser, position: { x: opp.x, y: opp.y - 1 } },
    ];
    this.game = new Game({ difficulty: d, seed, opponentRoster: roster });
    this.transition({ kind: "command_post", index: 0 });
    this.startTicking();
  }

  // ── Command post ─────────────────────────────────────────────────────────

  private commandPostLabel(i: number): { label: string; sample?: string } {
    const item = COMMAND_POST_ITEMS[i % COMMAND_POST_ITEMS.length]!;
    if (item.kind === "factory") return { label: "Factory", sample: SOUNDS.cmdFactory };
    if (item.kind === "messages") return { label: "Messages", sample: SOUNDS.cmdMessages };
    if (item.kind === "stop") return { label: "Stop game", sample: SOUNDS.cmdStopGame };
    const kind = item.data!;
    const samples: Partial<Record<UnitKind, string>> = {
      [UnitKind.Worker]: SOUNDS.cmdWorkers,
      [UnitKind.Truck]: SOUNDS.cmdTrucks,
      [UnitKind.Soldier]: SOUNDS.cmdSoldiers,
      [UnitKind.ElectronicGun]: SOUNDS.cmdLaserGuns,
      [UnitKind.RocketPlant]: SOUNDS.cmdMissiles,
      [UnitKind.SpaceTank]: SOUNDS.cmdSpaceTanks,
      [UnitKind.SpaceScout]: SOUNDS.cmdSpaceScouts,
      [UnitKind.SpaceCruiser]: SOUNDS.cmdSpaceCruisers,
    };
    return { label: pluralName(kind), sample: samples[kind] };
  }

  private renderCommandPost(): void {
    if (this.screen.kind !== "command_post" || !this.game) return;
    const { label, sample } = this.commandPostLabel(this.screen.index);
    this.setCurrent(label);
    this.narrate(label, sample);
  }

  // ── Factory ──────────────────────────────────────────────────────────────

  private renderFactory(): void {
    if (this.screen.kind !== "factory" || !this.game) return;
    const item = FACTORY_ITEMS[this.screen.index]!;
    const label = item.kind === "status" ? "Status" : kindName(item.data);
    this.setCurrent(label);
    if (item.kind === "status") {
      this.narrate(label, SOUNDS.factoryStatus);
      return;
    }
    const samples: Partial<Record<UnitKind, string>> = {
      [UnitKind.Worker]: SOUNDS.factoryWorker,
      [UnitKind.Truck]: SOUNDS.factoryTruck,
      [UnitKind.Soldier]: SOUNDS.factorySoldier,
      [UnitKind.ElectronicGun]: SOUNDS.factoryGun,
      [UnitKind.RocketPlant]: SOUNDS.factoryPlant,
      [UnitKind.SpaceTank]: SOUNDS.factoryTank,
      [UnitKind.SpaceScout]: SOUNDS.factoryScout,
      [UnitKind.SpaceCruiser]: SOUNDS.factoryCruiser,
    };
    this.narrate(label, samples[item.data]);
  }

  // ── Placement ────────────────────────────────────────────────────────────

  private renderPlacement(): void {
    if (!this.game || this.game.pendingPlacementUnitId === null) {
      this.transition({ kind: "command_post", index: 0 });
      return;
    }
    const u = this.game.humanUnits.find((x) => x.id === this.game!.pendingPlacementUnitId);
    if (!u) {
      this.transition({ kind: "command_post", index: 0 });
      return;
    }
    this.titleEl.textContent = `Place ${kindName(u.kind)}`;
    this.setCurrent(`${u.position.x}, ${u.position.y}`);
    this.speech.say(`Place ${kindName(u.kind)}. Position ${u.position.x}, ${u.position.y}.`);
  }

  // ── Driver ───────────────────────────────────────────────────────────────

  private renderDriver(): void {
    if (!this.game || this.game.driverKind === null) {
      this.transition({ kind: "command_post", index: 0 });
      return;
    }
    const kind = this.game.driverKind;
    this.titleEl.textContent = `Driving ${kindName(kind)}`;
    const u = this.game.activeUnit();
    if (!u) {
      this.setCurrent(`no ${pluralName(kind)}`);
      this.speech.say(`You have no ${pluralName(kind)}.`);
      return;
    }
    // Visual-only echo of the unit being driven — speech stays silent so
    // the player only hears the per-kind ambient loop, per manual.
    this.setCurrent(`${kindName(kind)} ${u.id}`);
  }

  private renderWeaponMenu(): void {
    if (this.screen.kind !== "weapon_menu" || !this.game) return;
    const u = this.game.activeUnit();
    if (!u) {
      this.transition({ kind: "driver" });
      return;
    }
    const list = WEAPONS[u.kind];
    const w = list[this.screen.index];
    if (!w) {
      this.transition({ kind: "driver" });
      return;
    }
    this.setCurrent(w.name);
    this.speech.say(w.name);
  }

  // ── Messages ─────────────────────────────────────────────────────────────

  private renderMessages(s: { kind: "messages"; readingArchive: boolean }): void {
    if (!this.game) return;
    this.titleEl.textContent = s.readingArchive ? "Old messages" : "New messages";
    const list = s.readingArchive ? this.game.messages.archiveList() : this.game.messages.pendingList();
    if (list.length === 0) {
      const msg = s.readingArchive ? "There are no old messages." : "There are no new messages.";
      this.setCurrent(msg);
      this.speech.say(msg);
      return;
    }
    const first = list[0]!.text;
    this.setCurrent(first);
    this.speech.say(first);
  }

  // ── Tick loop ────────────────────────────────────────────────────────────

  private startTicking(): void {
    this.stopTicking();
    this.tickTimer = window.setInterval(() => this.tickOnce(), this.tickIntervalMs);
  }

  private stopTicking(): void {
    if (this.tickTimer !== null) {
      window.clearInterval(this.tickTimer);
      this.tickTimer = null;
    }
  }

  private tickOnce(): void {
    if (!this.game || this.game.outcome !== "ongoing") return;
    const ev = this.game.tick();

    // Outcome change?
    if (ev.outcome === "human_wins" || ev.outcome === "opponent_wins") {
      this.stopTicking();
      this.transition({
        kind: "game_over",
        outcome: ev.outcome === "human_wins" ? "won" : "lost",
      });
      return;
    }

    // Incoming shell — play the appropriate spatial cue.
    if (ev.incoming) {
      const pan: SpatialPan = ev.incoming.direction;
      const active = this.game.activeUnit();
      if (active) {
        this.audio.play(driverSound(active.kind, ev.incoming.sound), { pan });
      }
      if (ev.incoming.killed) {
        this.speech.say(`Your ${active ? kindName(active.kind) : "unit"} was destroyed.`);
      }
    }

    // Target acquired — only when active unit acquires.
    if (ev.targetAcquired) {
      const active = this.game.activeUnit();
      if (active) {
        this.audio.play(driverSound(active.kind, "t_radar"), { pan: active.targetPointer });
        this.audio.play(enemyIdSound(ev.targetAcquired.targetKind), { gain: 0.8 });
      }
    }

    // New-message ping (once per arrival).
    if (this.game.messages.drainFreshFlag()) {
      this.audio.play(SOUNDS.newMessage, { gain: 0.6 });
    }

    // Drive-mode ambient sounds: if active unit is moving, occasional step sound.
    if (this.screen.kind === "driver") {
      const u = this.game.activeUnit();
      if (u && u.move.moving) {
        this.audio.play(driverSound(u.kind, "moving"), { gain: 0.5 });
      }
      // Re-render driver line so position updates land in speech if user queries.
      // (We don't auto-announce — only the title text is refreshed.)
      this.titleEl.textContent = u
        ? `Driving ${kindName(u.kind)} at ${u.position.x}, ${u.position.y}`
        : this.titleEl.textContent;
    }
  }

  // ── Key dispatcher ───────────────────────────────────────────────────────

  handleKey(ev: KeyboardEvent): void {
    if (this.screen.kind === "idle") return;
    const key = ev.key;
    const lower = key.toLowerCase();
    const numeric = key >= "0" && key <= "9";

    if (key === "Escape") {
      ev.preventDefault();
      this.handleEscape();
      return;
    }

    // Per the manual, Ctrl+N reads the next pending message without leaving
    // the current screen — works in command post, factory, and driver alike.
    if (
      ev.ctrlKey && lower === "n" && this.game &&
      (this.screen.kind === "command_post" || this.screen.kind === "factory" || this.screen.kind === "driver")
    ) {
      ev.preventDefault();
      this.readNextMessage();
      return;
    }

    switch (this.screen.kind) {
      case "main_menu":
        ev.preventDefault();
        if (key === "ArrowUp" || key === "ArrowDown") {
          const delta = key === "ArrowUp" ? -1 : 1;
          this.screen.index = (this.screen.index + delta + MAIN_MENU_ITEMS.length) % MAIN_MENU_ITEMS.length;
          this.renderMainMenu();
        } else if (key === "Enter") {
          this.mainMenuActivate();
        }
        return;

      case "difficulty":
        ev.preventDefault();
        if (key === "ArrowUp" || key === "ArrowDown") {
          const delta = key === "ArrowUp" ? -1 : 1;
          this.screen.index = (this.screen.index + delta + DIFFICULTY_OPTIONS.length) % DIFFICULTY_OPTIONS.length;
          this.renderDifficulty();
        } else if (key === "Enter") {
          this.startNewGame(DIFFICULTY_OPTIONS[this.screen.index]!.d);
        }
        return;

      case "command_post":
        ev.preventDefault();
        if (key === "ArrowUp" || key === "ArrowDown") {
          const delta = key === "ArrowUp" ? -1 : 1;
          this.screen.index = (this.screen.index + delta + COMMAND_POST_ITEMS.length) % COMMAND_POST_ITEMS.length;
          this.renderCommandPost();
        } else if (key === "Enter") return this.commandPostActivate();
        else if (key === " ") return this.commandPostQuery();
        else if (lower === "b") return this.speakBase();
        return;

      case "factory":
        ev.preventDefault();
        if (key === "ArrowUp" || key === "ArrowDown") {
          const delta = key === "ArrowUp" ? -1 : 1;
          this.screen.index = (this.screen.index + delta + FACTORY_ITEMS.length) % FACTORY_ITEMS.length;
          this.renderFactory();
        } else if (key === "Enter") return this.factoryActivate();
        else if (key === " ") return this.factoryStatus();
        return;

      case "placement":
        ev.preventDefault();
        return this.handlePlacementKey(key);

      case "driver":
        ev.preventDefault();
        if (numeric) return this.driverSwitchSlot(Number(key));
        return this.handleDriverKey(key, lower);

      case "weapon_menu":
        ev.preventDefault();
        if (key === "ArrowUp" || key === "ArrowDown") {
          const u = this.game?.activeUnit();
          if (!u) return;
          const list = WEAPONS[u.kind];
          const delta = key === "ArrowUp" ? -1 : 1;
          this.screen.index = (this.screen.index + delta + list.length) % list.length;
          this.renderWeaponMenu();
        } else if (key === "Enter") {
          // Per the manual: Enter inside the weapon menu fires immediately —
          // there is no separate "arm and exit" step.
          if (!this.game) return;
          this.game.selectWeapon(this.screen.index);
          this.game.closeWeaponMenu();
          this.screen = { kind: "driver" };
          this.handleDriverFire();
        }
        return;

      case "messages":
        ev.preventDefault();
        if (key === "ArrowDown") return this.readNextMessage();
        if (lower === "o") {
          this.screen = { kind: "messages", readingArchive: !this.screen.readingArchive };
          this.renderMessages(this.screen);
        }
        return;

      case "game_over":
        if (key === "Enter") {
          ev.preventDefault();
          this.game = null;
          this.transition({ kind: "main_menu", index: 0 });
        }
        return;
    }
  }

  private handleEscape(): void {
    switch (this.screen.kind) {
      case "main_menu":
        // Esc on the top menu is a no-op — without the start-button click we
        // can't reset audio cleanly. Stay put silently.
        return;
      case "difficulty":
        this.transition({ kind: "main_menu", index: 0 });
        return;
      case "command_post":
        // Esc in command post — stop game, go to main menu.
        this.stopTicking();
        this.audio.stopAllLoops();
        this.game = null;
        this.transition({ kind: "main_menu", index: 0 });
        return;
      case "factory":
        this.transition({ kind: "command_post", index: 0 });
        return;
      case "placement":
        // Can't escape placement — placement is mandatory.
        this.speech.say("You must place this unit before continuing.");
        return;
      case "driver":
        this.game?.exitDriver();
        this.transition({ kind: "command_post", index: 0 });
        return;
      case "weapon_menu":
        this.game?.closeWeaponMenu();
        this.transition({ kind: "driver" });
        return;
      case "messages":
        this.transition({ kind: "command_post", index: 0 });
        return;
      case "game_over":
        this.game = null;
        this.transition({ kind: "main_menu", index: 0 });
        return;
    }
  }

  // ── Menu activations ─────────────────────────────────────────────────────

  private mainMenuActivate(): void {
    if (this.screen.kind !== "main_menu") return;
    const item = MAIN_MENU_ITEMS[this.screen.index]!;
    switch (item) {
      case "play_with_computer":
        this.transition({ kind: "difficulty", index: 0 });
        return;
      case "play_with_partner":
        // Multiplayer matchmaking isn't ported yet — original used the dead
        // vipgameszone.com servers. Play the "not available" cue.
        this.audio.play(SOUNDS.notEnough);
        return;
      case "learn_game_sounds":
        // TODO: implement the sound-library browser. Stub for now.
        this.audio.play(SOUNDS.notEnough);
        return;
      case "final_score":
        this.speech.say(`Final score: ${this.game?.humanScore ?? 0} points.`);
        return;
      case "send_score":
        // Score upload required vipgameszone.com — gone.
        this.audio.play(SOUNDS.notEnough);
        return;
      case "test_speakers":
        // Per the manual, this verifies left/right speaker placement.
        // testspk/F_LEFT.wav is panned-left, F_RIGHT.wav panned-right; each
        // sample is ~1.06s, so we leave a ~0.6s gap between the two.
        this.audio.play(SOUNDS.testFrontLeft, { pan: "left" });
        window.setTimeout(() => this.audio.play(SOUNDS.testFrontRight, { pan: "right" }), 1700);
        return;
      case "get_id":
        // The original spoke the user's registered login. We've no config
        // flow yet — fall back to a generic stub.
        this.speech.say("Your ID is not configured yet.");
        return;
      case "quit":
        this.audio.stopAllLoops();
        this.titleEl.textContent = "Goodbye.";
        this.setHint("Refresh the page to play again.");
        this.transition({ kind: "idle" });
        return;
    }
  }

  private commandPostActivate(): void {
    if (this.screen.kind !== "command_post" || !this.game) return;
    const item = COMMAND_POST_ITEMS[this.screen.index]!;
    if (item.kind === "factory") {
      if (this.game.pendingPlacementUnitId !== null) {
        this.transition({ kind: "placement" });
      } else {
        this.transition({ kind: "factory", index: 0 });
      }
    } else if (item.kind === "messages") {
      this.transition({ kind: "messages", readingArchive: false });
    } else if (item.kind === "stop") {
      this.stopTicking();
      this.audio.stopAllLoops();
      this.game = null;
      this.transition({ kind: "main_menu", index: 0 });
    } else {
      const kind = item.data!;
      if (this.game.enterDriver(kind)) {
        this.transition({ kind: "driver" });
      } else {
        this.audio.play(SOUNDS.notEnough);
        this.speech.say(`You have no ${pluralName(kind)}.`);
      }
    }
  }

  private commandPostQuery(): void {
    if (this.screen.kind !== "command_post" || !this.game) return;
    const item = COMMAND_POST_ITEMS[this.screen.index]!;
    if (item.kind === "factory") {
      this.speech.say(`${this.game.humanMinerals} minerals at the factory.`);
    } else if (item.kind === "kind") {
      const n = this.game.unitsOfKind(item.data!).length;
      this.speech.say(`You have ${n} ${pluralName(item.data!)}.`);
    }
  }

  private factoryActivate(): void {
    if (this.screen.kind !== "factory" || !this.game) return;
    const item = FACTORY_ITEMS[this.screen.index]!;
    if (item.kind === "status") {
      this.speech.say(`${this.game.humanMinerals} minerals at the factory.`);
      return;
    }
    const r = this.game.produce(item.data);
    if (r.ok) {
      this.audio.play(SOUNDS.print);
      this.speech.say(`${kindName(item.data)} produced.`);
      // If static unit, jump to placement.
      if (this.game.pendingPlacementUnitId === r.unit.id) {
        this.transition({ kind: "placement" });
      }
    } else {
      this.audio.play(SOUNDS.notEnough);
      const why = ({
        insufficient_minerals: "Not enough minerals.",
        slot_cap_reached: "Slot limit reached.",
        difficulty_cap_reached: "Difficulty cap reached.",
        no_factory_room: "No room near the factory.",
      } as const)[r.reason];
      this.speech.say(why);
    }
  }

  private factoryStatus(): void {
    if (this.screen.kind !== "factory" || !this.game) return;
    const item = FACTORY_ITEMS[this.screen.index]!;
    if (item.kind === "status") {
      this.speech.say(`${this.game.humanMinerals} minerals at the factory.`);
    } else {
      const data = UNIT_DATA[item.data];
      this.speech.say(`${kindName(item.data)} costs ${data.cost} minerals.`);
    }
  }

  // ── Driver controls ──────────────────────────────────────────────────────

  private handleDriverKey(key: string, lower: string): void {
    if (!this.game) return;
    const u = this.game.activeUnit();
    if (!u) return;

    const isStaticUnit = !UNIT_DATA[u.kind].mobile;

    switch (key) {
      case "ArrowUp":
        if (this.game.startMove()) {
          this.audio.play(driverSound(u.kind, "moving"));
        }
        return;
      case "ArrowDown":
        this.game.stopMove();
        return;
      case "ArrowLeft":
        if (u.weaponMenuOpen) return;
        if (u.shieldTicks > 0 && !isStaticUnit) {
          this.game.moveShield("left");
        } else if (u.targetUnitId !== null) {
          this.game.moveTargetRadar("left");
          this.audio.play(driverSound(u.kind, "t_radar"), { pan: u.targetPointer });
        } else if (!isStaticUnit) {
          this.game.turnLeft();
        }
        return;
      case "ArrowRight":
        if (u.weaponMenuOpen) return;
        if (u.shieldTicks > 0 && !isStaticUnit) {
          this.game.moveShield("right");
        } else if (u.targetUnitId !== null) {
          this.game.moveTargetRadar("right");
          this.audio.play(driverSound(u.kind, "t_radar"), { pan: u.targetPointer });
        } else if (!isStaticUnit) {
          this.game.turnRight();
        }
        return;
      case " ":
        this.speech.say(`${kindName(u.kind)} at ${u.position.x}, ${u.position.y}.`);
        return;
      case "Tab":
        this.speech.say(`Energy ${u.energy}.`);
        return;
      case "Enter":
        return this.handleDriverFire();
      case "Delete":
        return this.handleDriverStrike();
    }

    switch (lower) {
      case "r":
        if (this.game.toggleSearchRadar()) {
          if (u.searchRadarOn) this.audio.play(driverSound(u.kind, "s_radar"));
          else this.speech.say("Radar off.");
        } else {
          this.speech.say("Cannot scan while moving.");
        }
        return;
      case "w":
        if (this.game.openWeaponMenu()) {
          this.transition({ kind: "weapon_menu", index: u.weaponSelected ?? 0 });
        }
        return;
      case "s": {
        this.game.toggleShield();
        const onOff = u.shieldTicks > 0 ? "on" : "off";
        this.audio.play(driverSound(u.kind, "energetic_shield"));
        this.speech.say(`Shield ${onOff}.`);
        return;
      }
      case "d":
        if (isStaticUnit) {
          if (u.targetUnitId !== null) {
            const target = this.game.opponentUnits.find((x) => x.id === u.targetUnitId);
            if (target) this.speech.say(`Distance ${chebyshev(u.position, target.position)}.`);
          }
        } else {
          this.speech.say(directionName(u.direction));
        }
        return;
      case "h":
        if (u.kind === UnitKind.RocketPlant && u.targetUnitId !== null) {
          const target = this.game.opponentUnits.find((x) => x.id === u.targetUnitId);
          if (target) this.speech.say(`Height ${shipHeight(target.kind, target.position)}.`);
        }
        return;
      case "k":
        if (u.targetUnitId !== null) {
          const t = this.game.opponentUnits.find((x) => x.id === u.targetUnitId);
          if (t) this.speech.say(`Enemy ${kindName(t.kind)}.`);
        } else {
          this.speech.say("No enemy locked.");
        }
        return;
      case "b":
        return this.speakBase();
      case "e":
        return this.speakEnemyBase();
      case "z":
        return this.speakWeaponHint();
      case "t":
        if (this.game.teleportToBase()) {
          this.audio.play(driverSound(u.kind, "teleport"));
          this.speech.say("Teleported to base.");
        } else {
          this.speech.say("Cannot teleport.");
        }
        return;
      case "x":
        return this.handleDriverStrike();
    }
  }

  /**
   * Fire the active unit's selected weapon. Per the manual all outcomes are
   * conveyed by *sound*: no target → silent; off-pointer → "beside the mark"
   * sample; wrong weapon → silent energy drop; hit/kill → weapon sample +
   * success sample.
   */
  private handleDriverFire(): void {
    if (!this.game) return;
    const u = this.game.activeUnit();
    if (!u) return;
    const r = this.game.fire();
    if (!r.hadLock) return;
    if (!r.onTarget) {
      this.audio.play(driverSound(u.kind, "not"));
      return;
    }
    if (!r.rightWeapon) return; // silent — energy was already lost in the engine
    if (r.sound) this.audio.play(driverSound(u.kind, r.sound));
    if (r.attack?.killed) this.audio.play(driverSound(u.kind, "kill"));
    else if (r.attack?.absorbed) this.audio.play(driverSound(u.kind, "not"));
    else if (r.attack?.hit) this.audio.play(driverSound(u.kind, "hit"));
  }

  private handleDriverStrike(): void {
    if (!this.game) return;
    const strike = this.game.strikeOpponentBase();
    if (!strike) {
      this.speech.say("Not on the enemy base.");
      return;
    }
    if (strike.gameOver) {
      this.audio.play(SOUNDS.victory1);
      this.transition({ kind: "game_over", outcome: "won" });
    } else {
      this.speech.say(`Strike for ${strike.score} points.`);
    }
  }

  private driverSwitchSlot(slot: number): void {
    if (!this.game) return;
    const u = this.game.switchActiveSlot(slot);
    if (!u) {
      this.audio.play(SOUNDS.notEnough);
      this.speech.say(`${pluralName(this.game.driverKind ?? UnitKind.Worker)} ${slot} does not exist.`);
      return;
    }
    this.speech.say(`Active ${kindName(u.kind)} ${slot}.`);
  }

  // ── Placement controls ───────────────────────────────────────────────────

  private handlePlacementKey(key: string): void {
    if (!this.game || this.game.pendingPlacementUnitId === null) return;
    const u = this.game.humanUnits.find((x) => x.id === this.game!.pendingPlacementUnitId);
    if (!u) return;
    let dx = 0;
    let dy = 0;
    if (key === "ArrowUp") dy = 1;
    else if (key === "ArrowDown") dy = -1;
    else if (key === "ArrowLeft") dx = -1;
    else if (key === "ArrowRight") dx = 1;
    else if (key === "Enter") {
      if (this.game.placeStaticAt(u.position)) {
        this.speech.say(`${kindName(u.kind)} placed at ${u.position.x}, ${u.position.y}.`);
        this.transition({ kind: "command_post", index: 0 });
      } else {
        this.audio.play(SOUNDS.notEnough);
        this.speech.say("Cannot place here.");
      }
      return;
    } else if (key === " ") {
      this.speech.say(`Position ${u.position.x}, ${u.position.y}.`);
      return;
    } else {
      return;
    }
    const dest = { x: u.position.x + dx, y: u.position.y + dy };
    const base = this.game.board.bases[0];
    if (chebyshev(dest, base) > 2) {
      this.audio.play(SOUNDS.notEnough);
      this.speech.say("Too far from base.");
      return;
    }
    if (this.game.board.moveTo(u, dest)) {
      this.speech.say(`${dest.x}, ${dest.y}.`);
    } else {
      this.audio.play(SOUNDS.notEnough);
      this.speech.say("Cell occupied.");
    }
  }

  // ── Queries ──────────────────────────────────────────────────────────────

  private speakBase(): void {
    if (!this.game) return;
    const b = this.game.board.bases[0];
    this.speech.say(`Your base is at ${b.x}, ${b.y}.`);
  }

  private speakEnemyBase(): void {
    if (!this.game) return;
    // Per the manual, E only works after a scout has actually found the
    // enemy base. Until then the player gets the "no info" cue.
    if (!this.game.enemyBaseKnown) {
      this.audio.play(SOUNDS.notEnough);
      return;
    }
    const b = this.game.board.bases[1];
    this.speech.say(`Enemy base is at ${b.x}, ${b.y}.`);
  }

  private speakWeaponHint(): void {
    if (!this.game) return;
    const u = this.game.activeUnit();
    if (!u || u.targetUnitId === null) {
      this.speech.say("No enemy locked.");
      return;
    }
    const target = this.game.opponentUnits.find((x) => x.id === u.targetUnitId) as Unit | undefined;
    if (!target) return;
    const rec = recommendWeapon(u, target);
    if (rec) this.speech.say(rec.hint);
    else this.speech.say("No suitable weapon for that target.");
  }

  private readNextMessage(): void {
    if (!this.game) return;
    const m = this.game.messages.readNext();
    if (m) this.speech.say(m.text);
    else this.speech.say("No new messages.");
  }
}

// ── Helpers ───────────────────────────────────────────────────────────────

function pluralName(kind: UnitKind): string {
  switch (kind) {
    case UnitKind.Worker: return "workers";
    case UnitKind.Truck: return "trucks";
    case UnitKind.Soldier: return "soldiers";
    case UnitKind.ElectronicGun: return "electronic guns";
    case UnitKind.RocketPlant: return "rocket plants";
    case UnitKind.SpaceTank: return "space tanks";
    case UnitKind.SpaceScout: return "space scouts";
    case UnitKind.SpaceCruiser: return "space cruisers";
  }
}

function directionName(d: number): string {
  return [
    "north", "north-east", "east", "south-east",
    "south", "south-west", "west", "north-west",
  ][d - 1] ?? "north";
}

// ── Boot ──────────────────────────────────────────────────────────────────

const titleEl = document.getElementById("title") as HTMLElement;
const currentEl = document.getElementById("current") as HTMLElement;
const hintEl = document.getElementById("hint") as HTMLElement;
const startBtn = document.getElementById("start") as HTMLButtonElement;

const app = new App(titleEl, currentEl, hintEl);

startBtn.addEventListener("click", async () => {
  startBtn.style.display = "none";
  await app.startGame();
});

// The autofocused start button receives Enter directly as a click, so the
// document keydown listener won't fire for that very first Enter. Once the
// button is hidden, focus drops to body and document-level keys take over.
document.addEventListener("keydown", (ev) => {
  app.handleKey(ev);
});
